import { DEFAULT_ROUTE } from "@zentrack/config";
import type { AppSettings } from "@zentrack/desktop-api";
import type {
  ActiveSession,
  AppUsage,
  Client,
  Project,
  ProjectSoftwareRule,
  TimeEntry,
} from "@zentrack/domain";

/**
 * Persisted browser state. One instance of this object is stored under a single
 * key in IndexedDB. This mirrors the previous localStorage-backed shape so the
 * behaviour stays identical, but the storage backend is now IndexedDB.
 */
export interface BrowserState {
  clients: Client[];
  projects: Project[];
  projectSoftwareRules: ProjectSoftwareRule[];
  timeEntries: TimeEntry[];
  appUsages: AppUsage[];
  activeSession: ActiveSession | null;
  settings: AppSettings;
  schemaVersion: number;
}

export const SCHEMA_VERSION = 1;

export function defaultState(): BrowserState {
  return {
    clients: [],
    projects: [],
    projectSoftwareRules: [],
    timeEntries: [],
    appUsages: [],
    activeSession: null,
    settings: {
      lastRoute: DEFAULT_ROUTE,
      autoPauseOnIdle: false,
      idleThresholdMinutes: 5,
    },
    schemaVersion: SCHEMA_VERSION,
  };
}

/**
 * Merge a partially-persisted state with defaults. Older snapshots that lack
 * newer fields fall back to the default values, and the schemaVersion is
 * normalised so future migrations can detect the source version.
 */
export function hydrateState(raw: Partial<BrowserState> | null | undefined): BrowserState {
  if (!raw) {
    return defaultState();
  }

  return {
    ...defaultState(),
    ...raw,
    settings: {
      ...defaultState().settings,
      ...(raw.settings ?? {}),
    },
    schemaVersion: SCHEMA_VERSION,
  };
}
